/**
 * PHP Data Access Layer
 *
 * Safe accessors for PHP-injected global data.
 * This layer provides type-safe access and null-safety checks.
 */

import { translator } from "../translation/translator";
import type {
    PhpAppContext,
    PhpAuthUser,
    PhpBoardConfig,
    PhpBrandData,
    PhpMediaPaths,
    PhpPostData,
    PhpPostDataRaw,
} from "../types/php-injected";
import type { QuillConstructor } from "../types/quill";

type PostData = import("../post/post-types").PostData;
type PostPatch = Partial<PostData>;
type RecommendBadgeTextMap = { new?: string; recommend?: string; rank?: string };
type PostsDataMap = Record<string, PhpPostData>;
type RawPostsDataMap = Record<string, PhpPostDataRaw>;
type TranslationDictionary = Record<string, Record<string, string>>;
type PhpAuthUserCompat = PhpAuthUser & { id?: number; is_admin?: boolean | number };
type PhpAvatarPaths = NonNullable<PhpMediaPaths["avatar"]>;
type PhpProductPaths = NonNullable<PhpMediaPaths["product"]>;
type PhpBrandPaths = NonNullable<PhpMediaPaths["brand"]>;
type PhpPostPaths = NonNullable<PhpMediaPaths["post"]>;
type UnknownRecord = Record<string, unknown>;
type RecommendBadgeTextMapInput = { new?: unknown; recommend?: unknown; rank?: unknown };
type PhpSnsLinkInput = { platform?: unknown; url?: unknown; url_type?: unknown };
type PhpRecommendBadgeConfigInput = {
    showRankDefault?: unknown;
    maxRecommendRank?: unknown;
    captions?: unknown;
    icons?: unknown;
};

interface RecommendBadgeConfig {
    showRankDefault?: boolean;
    maxRecommendRank?: number;
    captions?: RecommendBadgeTextMap;
    icons?: RecommendBadgeTextMap;
}

interface PhpDataRoot {
    recommendBadge?: RecommendBadgeConfig;
}

type WindowWithPhpGlobals = Window & {
    postsData?: RawPostsDataMap;
    __normalizedPostsData?: PostsDataMap;
    AppContext?: PhpAppContext & { countryOptions?: string[] };
    authUser?: PhpAuthUserCompat;
    MediaPaths?: PhpMediaPaths;
    boardConfig?: PhpBoardConfig;
    brandsData?: PhpBrandData[];
    phpData?: PhpDataRoot;
    translate?: (translateKey: string, lang?: string) => string;
};

interface PhpUrlBaseHelper {
    avatar(): string | null;
    productImages(): string | null;
    productNoImage(): string | null;
    brandImages(): string | null;
    postImages(): string | null;
    postVideos(): string | null;
}

interface PhpUrlHelper {
    base: PhpUrlBaseHelper;
    avatar(avatar?: string | null): string | null;
    postImage(boardId?: string | null, userId?: number | null, filename?: string | null): string | null;
    postVideo(boardId?: string | null, userId?: number | null, filename?: string | null): string | null;
    productImage(brandIdOrFilename?: string | number | null, optionalFilename?: string | null): string | null;
    productNoImage(): string | null;
    brandImage(filename?: string | null): string | null;
}

function getPhpWindow(): WindowWithPhpGlobals {
    return window as WindowWithPhpGlobals;
}

function isRecord(value: unknown): value is UnknownRecord {
    return !!value && typeof value === "object" && !Array.isArray(value);
}

function isSnsPlatform(
    value: string,
): value is NonNullable<NonNullable<PhpPostData["sns_links"]>[number] & object>["platform"] {
    return ["facebook", "youtube", "instagram", "x", "tiktok"].includes(value);
}

function isSnsUrlType(
    value: string,
): value is NonNullable<NonNullable<PhpPostData["sns_links"]>[number] & object>["url_type"] {
    return ["post", "video", "shorts", "photo", "page", "event", "reel", "story"].includes(value);
}

function normalizeRecommendBadgeTextMap(value: unknown): RecommendBadgeTextMap | null {
    if (!isRecord(value)) {
        return null;
    }

    const source = value as RecommendBadgeTextMapInput;
    const next: RecommendBadgeTextMap = {};
    const newText = source.new;
    const recommendText = source.recommend;
    const rankText = source.rank;

    if (typeof newText === "string") next.new = newText;
    if (typeof recommendText === "string") next.recommend = recommendText;
    if (typeof rankText === "string") next.rank = rankText;

    return next;
}

function normalizePhpSnsLinks(value: unknown): PhpPostData["sns_links"] | undefined {
    if (!Array.isArray(value)) {
        return undefined;
    }

    const next: NonNullable<PhpPostData["sns_links"]> = [];
    value.forEach((entry) => {
        if (typeof entry === "string") {
            next.push(entry);
            return;
        }
        if (!isRecord(entry)) {
            return;
        }

        const source = entry as PhpSnsLinkInput;
        const platform = source.platform;
        const url = source.url;
        const urlType = source.url_type;

        if (
            typeof platform === "string" &&
            typeof url === "string" &&
            typeof urlType === "string" &&
            isSnsPlatform(platform) &&
            isSnsUrlType(urlType)
        ) {
            next.push({ platform, url, url_type: urlType });
            return;
        }

        if (typeof url === "string") {
            next.push(url);
        }
    });

    return next;
}

function normalizeNumericId(value: unknown): number | undefined {
    const normalizedValue = Number(value);
    return Number.isInteger(normalizedValue) && normalizedValue > 0 ? normalizedValue : undefined;
}

function normalizeNullableNumericId(value: unknown): number | null | undefined {
    if (value === null) {
        return null;
    }

    if (value === undefined || value === "") {
        return undefined;
    }

    const normalizedValue = normalizeNumericId(value);
    return normalizedValue ?? undefined;
}

function normalizeReviewData(value: unknown): PhpPostData["review_data"] | undefined {
    if (!isRecord(value)) {
        return undefined;
    }

    const normalizedReviewData: UnknownRecord = { ...value };
    if ("product_id" in value) {
        const normalizedProductId = normalizeNullableNumericId(value.product_id);
        if (normalizedProductId !== undefined) {
            normalizedReviewData.product_id = normalizedProductId;
        } else {
            delete normalizedReviewData.product_id;
        }
    }

    return normalizedReviewData as PhpPostData["review_data"];
}

function normalizePhpPostData(value: unknown): PhpPostData {
    const normalized: PhpPostData = {};
    if (!isRecord(value)) {
        return normalized;
    }

    const keys = [
        "id",
        "post_id",
        "board_id",
        "user_id",
        "post_product_id",
        "review_data",
        "post_title",
        "content",
        "attached_images",
        "attached_videos",
        "content_images",
        "likes_count",
        "is_liked_by_current_user",
        "reads_count",
        "comments_count",
        "average_rating",
        "board",
        "product",
    ] as const;

    keys.forEach((key) => {
        const fieldValue = value[key];
        if (fieldValue === undefined) {
            return;
        }

        switch (key) {
            case "id":
            case "post_id":
            case "user_id": {
                const normalizedId = normalizeNumericId(fieldValue);
                if (normalizedId !== undefined) {
                    normalized[key] = normalizedId;
                }
                return;
            }
            case "post_product_id": {
                const normalizedPostProductId = normalizeNullableNumericId(fieldValue);
                if (normalizedPostProductId !== undefined) {
                    normalized.post_product_id = normalizedPostProductId;
                }
                return;
            }
            case "review_data": {
                const normalizedReviewData = normalizeReviewData(fieldValue);
                if (normalizedReviewData !== undefined) {
                    normalized.review_data = normalizedReviewData;
                }
                return;
            }
            default:
                (normalized as UnknownRecord)[key] = fieldValue;
        }
    });

    const snsLinks = normalizePhpSnsLinks(value.sns_links);
    if (snsLinks) {
        normalized.sns_links = snsLinks;
    }

    const comments = value.comments;
    if (Array.isArray(comments) || isRecord(comments)) {
        normalized.comments = comments as PhpPostData["comments"];
    }

    return normalized;
}

function normalizePhpPostsDataMap(value: RawPostsDataMap): PostsDataMap {
    const entries = Object.entries(value).map(([postId, post]) => [postId, normalizePhpPostData(post)]);
    return Object.fromEntries(entries);
}

function normalizeUrlPath(urlPath: unknown): string | null {
    if (typeof urlPath !== "string") return null;
    const trimmed = urlPath.trim();
    if (!trimmed) return null;
    return trimmed.endsWith("/") ? trimmed.slice(0, -1) : trimmed;
}

export function phpPostsData(): PostsDataMap | null {
    const phpWindow = getPhpWindow();
    const postsData = phpWindow.postsData;
    if (!postsData) {
        console.warn("[PHP Data] window.postsData not initialized by PHP");
        return null;
    }

    if (!isRecord(postsData)) {
        return null;
    }

    if (!phpWindow.__normalizedPostsData) {
        phpWindow.__normalizedPostsData = normalizePhpPostsDataMap(postsData as RawPostsDataMap);
    }

    return phpWindow.__normalizedPostsData;
}

export function phpPostData(postId: number): PhpPostData | null {
    const postsData = phpPostsData();
    if (!postsData) {
        console.warn("[PHP Data] Cannot access post data, postsData not initialized");
        return null;
    }

    const post = postsData[String(postId)];
    if (!post) {
        console.warn(`[PHP Data] Post ${postId} not found in global postsData`);
        return null;
    }

    return post;
}

export function phpPatchPostData(postId: number, patch: PostPatch): boolean {
    try {
        const postsData = phpPostsData();
        if (!postsData) return false;

        const key = String(postId);
        const target = postsData[key];
        if (!target || typeof target !== "object") return false;
        if (!patch || typeof patch !== "object") return false;

        Object.assign(target, patch);
        return true;
    } catch (_error) {
        return false;
    }
}

export function getPostData(postId: number): PhpPostData | null {
    return phpPostData(postId);
}

export function phpAuthUser(): PhpAuthUserCompat | null {
    const authUser = getPhpWindow().authUser;
    if (!authUser) {
        console.warn("[PHP Data] global authUser not initialized by PHP");
        return null;
    }

    return authUser;
}

export function phpQuill(): QuillConstructor | null {
    const quill = (window as Window & { Quill?: QuillConstructor }).Quill;
    return quill ? (quill as QuillConstructor) : null;
}

export function phpAppContext(): (PhpAppContext & { countryOptions?: string[] }) | null {
    const context = getPhpWindow().AppContext;
    if (!context) {
        console.warn("[PHP Data] window.AppContext not initialized by PHP");
        return null;
    }
    return context;
}

export function phpRecommendBadgeConfig(): RecommendBadgeConfig | null {
    const root = getPhpWindow().phpData;
    if (!root || typeof root !== "object" || Array.isArray(root)) {
        return null;
    }

    const config = (root as { recommendBadge?: unknown }).recommendBadge;
    if (!isRecord(config)) {
        return null;
    }

    const source = config as PhpRecommendBadgeConfigInput;
    const showRankDefault = source.showRankDefault;
    const maxRecommendRankRaw = Number(source.maxRecommendRank);
    const captions = normalizeRecommendBadgeTextMap(source.captions);
    const icons = normalizeRecommendBadgeTextMap(source.icons);

    const normalized: RecommendBadgeConfig = {};
    if (typeof showRankDefault === "boolean") {
        normalized.showRankDefault = showRankDefault;
    }
    if (Number.isFinite(maxRecommendRankRaw)) {
        normalized.maxRecommendRank = maxRecommendRankRaw;
    }
    if (captions && Object.keys(captions).length > 0) {
        normalized.captions = captions;
    }
    if (icons && Object.keys(icons).length > 0) {
        normalized.icons = icons;
    }

    return normalized;
}

export function phpMaxRecommendRank(): number {
    const value = Number(phpRecommendBadgeConfig()?.maxRecommendRank);
    if (!Number.isFinite(value) || value <= 0) {
        return 15;
    }
    return Math.trunc(value);
}

export function phpRecommendBadgeCaptions(): RecommendBadgeTextMap | null {
    const config = phpRecommendBadgeConfig();
    const captions = normalizeRecommendBadgeTextMap(config?.captions);
    return captions && Object.keys(captions).length > 0 ? captions : null;
}

export function phpRecommendBadgeIcons(): RecommendBadgeTextMap | null {
    const config = phpRecommendBadgeConfig();
    const icons = normalizeRecommendBadgeTextMap(config?.icons);
    return icons && Object.keys(icons).length > 0 ? icons : null;
}

export function phpUserLocale(): string | null {
    return phpAppContext()?.locale || null;
}

export function phpUserTimezone(): string | null {
    return phpAppContext()?.timezone || null;
}

export function phpClientContextUrl(): string | null {
    return phpAppContext()?.clientContextUrl || null;
}

export function phpCsrfToken(): string | null {
    return phpAppContext()?.csrfToken || null;
}

export function phpCountryOptions(): string[] {
    const options = phpAppContext()?.countryOptions;
    return Array.isArray(options) ? options.filter((value) => typeof value === "string") : [];
}

export function getAuthUser(): PhpAuthUserCompat | null {
    return phpAuthUser();
}

function getPhpMediaPathsRoot(): PhpMediaPaths | null {
    const mediaPaths = getPhpWindow().MediaPaths;
    if (!mediaPaths) {
        console.warn("[PHP Data] global MediaPaths not initialized by PHP");
        return null;
    }

    return mediaPaths;
}

export function phpMediaPaths(
    type?: keyof PhpMediaPaths,
): PhpMediaPaths | PhpAvatarPaths | PhpProductPaths | PhpPostPaths | null {
    const mediaPaths = getPhpMediaPathsRoot();
    if (!mediaPaths) {
        return null;
    }

    if (type && !mediaPaths[type]) {
        console.warn(`[PHP Data] MediaPaths.${String(type)} not configured`);
        return null;
    }

    return type ? mediaPaths[type] || null : mediaPaths;
}

export function phpAvatarPaths(): PhpAvatarPaths | null {
    const mediaPaths = getPhpMediaPathsRoot();
    return mediaPaths?.avatar || null;
}

export function phpProductPaths(): PhpProductPaths | null {
    const mediaPaths = getPhpMediaPathsRoot();
    return mediaPaths?.product || null;
}

export function phpBrandPaths(): PhpBrandPaths | null {
    const mediaPaths = getPhpMediaPathsRoot();
    return mediaPaths?.brand || null;
}

export function phpPostPaths(): PhpPostPaths | null {
    const mediaPaths = getPhpMediaPathsRoot();
    return mediaPaths?.post || null;
}

const phpUrlBase: PhpUrlBaseHelper = {
    avatar(): string | null {
        const avatarPaths = phpAvatarPaths();
        return normalizeUrlPath(avatarPaths?.urlPath || null);
    },
    productImages(): string | null {
        const productPaths = phpProductPaths();
        return normalizeUrlPath(productPaths?.images?.urlPath || null);
    },
    productNoImage(): string | null {
        const productPaths = phpProductPaths();
        return normalizeUrlPath(productPaths?.images?.noImageUrl || null);
    },
    brandImages(): string | null {
        const brandPaths = phpBrandPaths();
        return normalizeUrlPath(brandPaths?.images?.urlPath || null);
    },
    postImages(): string | null {
        const postPaths = phpPostPaths();
        return normalizeUrlPath(postPaths?.images?.urlPath || null);
    },
    postVideos(): string | null {
        const postPaths = phpPostPaths();
        return normalizeUrlPath(postPaths?.videos?.urlPath || null);
    },
};

export const phpUrl: PhpUrlHelper = {
    base: phpUrlBase,
    avatar(avatar?: string | null): string | null {
        if (avatar === null || avatar === undefined || String(avatar).trim() === "") {
            const base = phpUrlBase.avatar();
            const avatarPaths = phpAvatarPaths();
            const defaultFilename = String(avatarPaths?.defaultFilename || "").trim();
            return base && defaultFilename ? `${base}/${defaultFilename}` : null;
        }

        const value = String(avatar).trim();
        if (value.startsWith("/") || value.startsWith("http")) return value;

        const base = phpUrlBase.avatar();
        return base ? `${base}/${value}` : null;
    },
    postImage(boardId?: string | null, userId?: number | null, filename?: string | null): string | null {
        if (!boardId || !userId || !filename) return null;
        const base = phpUrlBase.postImages();
        return base ? `${base}/${boardId}/${userId}/${filename}` : null;
    },
    postVideo(boardId?: string | null, userId?: number | null, filename?: string | null): string | null {
        if (!boardId || !userId || !filename) return null;
        const base = phpUrlBase.postVideos();
        return base ? `${base}/${boardId}/${userId}/${filename}` : null;
    },
    productImage(brandIdOrFilename?: string | number | null, optionalFilename?: string | null): string | null {
        const brandId = optionalFilename === undefined ? null : brandIdOrFilename;
        const filename = optionalFilename === undefined ? brandIdOrFilename : optionalFilename;

        if (filename === null || filename === undefined) return null;
        const value = String(filename).trim().replace(/\\/g, "/");
        if (!value) return null;
        if (value.startsWith("http")) return value;

        const base = phpUrlBase.productImages();
        if (base) {
            const normalizedBase = String(base).replace(/\\/g, "/").replace(/\/+$/, "");

            if (value.startsWith(normalizedBase)) {
                if (!brandId) return value;

                const expectedPrefix = `${normalizedBase}/${brandId}/`;
                if (value.startsWith(expectedPrefix)) return value;

                const normalized = value.startsWith("/") ? value.slice(1) : value;
                const filenameOnly = normalized.split("/").filter(Boolean).pop() || "";
                return filenameOnly ? `${normalizedBase}/${brandId}/${filenameOnly}` : value;
            }

            const normalized = value.startsWith("/") ? value.slice(1) : value;
            const filenameOnly = normalized.split("/").filter(Boolean).pop() || "";
            if (!filenameOnly) return null;

            if (brandId) return `${normalizedBase}/${brandId}/${filenameOnly}`;
            return `${normalizedBase}/${filenameOnly}`;
        }

        if (brandId) {
            const normalized = value.startsWith("/") ? value : `/${value}`;
            const prefix = "/media/products/";
            if (normalized.startsWith(prefix)) {
                const rest = normalized.slice(prefix.length);
                const parts = rest.split("/").filter(Boolean);
                if (parts.length >= 2 && String(parts[0]) === String(brandId)) return normalized;

                const filenameOnly = parts.pop() || "";
                return filenameOnly ? `${prefix}${brandId}/${filenameOnly}` : normalized;
            }
        }

        return value.startsWith("/") ? value : `/${value}`;
    },
    productNoImage(): string | null {
        return phpUrlBase.productNoImage();
    },
    brandImage(filename?: string | null): string | null {
        const value = String(filename || "").trim();
        if (!value) return null;
        if (/^https?:\/\//i.test(value)) return value;
        if (/^\/\//.test(value)) return `${location.protocol}${value}`;
        if (value.startsWith("/") || value.includes("/") || value.includes("\\")) {
            const normalized = value.replace(/\\/g, "/");
            return normalized.startsWith("/") ? normalized : `/${normalized}`;
        }

        const base = phpUrlBase.brandImages();
        return base ? `${base}/${value}` : null;
    },
};

export function phpAvatarUrl(avatar?: string | null): string | null {
    return phpUrl.avatar(avatar);
}

export function phpPostImageUrl(
    boardId?: string | null,
    userId?: number | null,
    filename?: string | null,
): string | null {
    return phpUrl.postImage(boardId, userId, filename);
}

export function phpPostVideoUrl(
    boardId?: string | null,
    userId?: number | null,
    filename?: string | null,
): string | null {
    return phpUrl.postVideo(boardId, userId, filename);
}

export function phpPostImagesUrlPath(): string | null {
    return phpUrl.base.postImages();
}

export function getMediaPaths(
    type?: keyof PhpMediaPaths,
): PhpMediaPaths | PhpAvatarPaths | PhpProductPaths | PhpPostPaths | null {
    return phpMediaPaths(type);
}

export function phpBoardConfig(): PhpBoardConfig | null {
    const boardConfig = getPhpWindow().boardConfig;
    if (!boardConfig) {
        console.warn("[PHP Data] global boardConfig not initialized by PHP");
        return null;
    }
    return boardConfig;
}

export function getBoardConfig(): PhpBoardConfig | null {
    return phpBoardConfig();
}

export function phpBrands(): PhpBrandData[] {
    const brandsData = getPhpWindow().brandsData;
    if (!brandsData) {
        console.warn("[PHP Data] global brandsData not initialized by PHP");
        return [];
    }
    return brandsData;
}

export function getBrands(): PhpBrandData[] {
    return phpBrands();
}

export function phpDict(dictName: string): TranslationDictionary | null {
    const keyMap: Record<string, string> = {
        Posts: "posts",
        Comments: "comments",
        UI: "ui",
        Navigation: "navigation",
        Boards: "boards",
        Products: "products",
        Users: "users",
        Attachments: "attachments",
        Modals: "modals",
        FormValidation: "formValidation",
    };

    const translatorKey = keyMap[dictName] || dictName.toLowerCase();
    const dictionaries = (translator.dictionaries || {}) as Record<string, TranslationDictionary | undefined>;
    const dict = dictionaries[translatorKey];

    if (!dict) {
        console.warn(`[PHP Data] Dictionary "${dictName}" (key: ${translatorKey}) not found in translator`);
        return null;
    }

    return dict;
}

export function getTranslationDict(dictName: string): TranslationDictionary | null {
    return phpDict(dictName);
}

export function phpTranslate(translateKey: string, lang?: string): string | null {
    const translate = getPhpWindow().translate;
    if (typeof translate !== "function") {
        return null;
    }

    try {
        const translated = translate(translateKey, lang);
        return translated !== translateKey ? translated : null;
    } catch (error) {
        console.warn("[PHP Data] Error calling global translate function:", error);
        return null;
    }
}
